import React, { Suspense, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Form, Header, Input, Select } from 'semantic-ui-react';
import { useStore } from './../../app/stores/store';
import { userLanguage } from '../../app/common/userLanguageSchema/userLanguage';
import { MetaDataElement } from '../../app/models/metaDataConfig';
import agent from '../../app/api/agent';
import { iconDetails } from '../DashboardConfiguration/settings/fixture';
import './styles.css';
import { toast } from 'react-toastify';
import LoadingComponent from '../../app/layout/LoadingComponent';
import UnauthorizedMessage from '../UnauthorizedMessage';
interface Props {
  isMaster: boolean;
}
const FormConfiguration = React.lazy(() => import('./form'));

function MasterRegisterForm({ isMaster }: Props) {
  const { commonStore, dashboardStore } = useStore();

  const [loader, setLoader] = useState({
    submit: false,
    initial: false,
  });
  const [formDetails, setFormDetails] = useState<{
    title: string;
    icon: string;
    dateTime: string;
    metadata: MetaDataElement[];
  }>({
    title: '',
    icon: '',
    dateTime: '',
    metadata: [],
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [masterList, setMasterList] = useState<string[]>([]);

  useEffect(() => {
    setLoader((p) => ({
      ...p,
      initial: true,
    }));
    (async () => {
      let result = await agent.dynamicMaster.getMasterDetailsAPI();

      if (result.data) {
        setMasterList(
          result.data?.map((data: { title: string }) => data.title),
        );
      }

      setLoader((p) => ({
        ...p,
        initial: false,
      }));
    })();
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    handleChange('', '');
    handleCancel();
    // eslint-disable-next-line
  }, [isMaster]);

  //-------------------------------> handle Cancel
  const handleCancel = () => {
    setFormDetails({
      title: '',
      icon: '',
      dateTime: '',
      metadata: [],
    });
  };

  //-------------------------------> handle Change
  const handleChange = (_e: any, data: any) => {
    let errMsg = '';

    if (data?.value !== undefined) {
      const lowercaseMasterList = masterList.map((item) => item.toLowerCase());
      if (
        lowercaseMasterList.includes(data.value?.toLowerCase()) ||
        (isMaster && ['worklevel', 'items'].includes(data.value?.toLowerCase()))
      ) {
        errMsg = 'The name already exist';
      } else if (!!data.value?.charAt(0).match(/\s/)) {
        errMsg = "The title shouldn't be started with space";
      } else if (data.value?.length === 0) {
        errMsg = 'Please fill the title';
      }
    }

    setFormDetails((p) => ({
      ...p,
      title: data?.value,
    }));
    setErrorMessage(errMsg);
  };

  const saveHandler = async () => {
    setLoader((p) => ({
      ...p,
      submit: true,
    }));

    let payload = {
      title: formDetails.title,
      icon: formDetails.icon,
      dateTime: new Date().toISOString(),
      metadata: formDetails.metadata,
    };

    if (isMaster) {
      await agent.dynamicMaster.postMasterDetailsAPI(payload);
      toast.success(`${formDetails.title} master has been created`);
    } else {
      await agent.dynamicMaster.postItemGroupDetailsAPI({
        title: formDetails.title,
      });
      toast.success(`${formDetails.title} has been created`);
    }

    setLoader((p) => ({
      ...p,
      submit: false,
    }));
    handleCancel();
  };
  return (
    <>
      {dashboardStore.dashboard.loadingdata === 'loading' && (
        <div style={{ marginTop: '30px' }}>
          <LoadingComponent />
        </div>
      )}
      {dashboardStore.dashboard.loadingdata !== 'loading' &&
        ((isMaster && !commonStore.filterModeOne?.includes('AVIB07')) ||
          (!isMaster && !commonStore.filterModeOne?.includes('AWLB01'))) && (
          <UnauthorizedMessage />
        )}
      {
        <div className="master-report-form-wrapper">
          <Header
            icon="edit"
            size="huge"
            dividing
            as="h1"
            content={isMaster ? 'Create Master' : 'Register Item Group'}
            sub
            color="grey"
          />
          <Form className="ui form error" autoComplete="off">
            <Form.Field
              control={Input}
              value={formDetails.title}
              onChange={handleChange}
              error={errorMessage || null}
              placeholder="Title"
              label="Title"
              name="title"
            />
            {isMaster && (
              <Form.Field
                label={'Select Icon'}
                control={Select}
                placeholder={'Select Icon'}
                clearable
                fluid
                search
                selection
                options={iconDetails}
                onKeyDown={(e: { keyCode: string; stopPropagation: any }) => {
                  if (e.keyCode === 'Backspace' || e.keyCode === 'Delete')
                    e.stopPropagation();
                }}
                style={{ marginTop: '5px' }}
                value={formDetails.icon}
                onChange={(_e: any, data: any) =>
                  setFormDetails((p) => ({
                    ...p,
                    icon: data.value,
                  }))
                }
              />
            )}

            {isMaster && (
              <Suspense fallback={<></>}>
                <FormConfiguration
                  formDetails={formDetails.metadata}
                  onChangeHandler={(data) => {
                    setFormDetails((p) => ({
                      ...p,
                      metadata: data,
                    }));
                  }}
                />
              </Suspense>
            )}
          </Form>
          <Button.Group style={{ margin: '0 0 0 auto' }}>
            <Button
              disabled={loader.submit}
              type="reset"
              onClick={handleCancel}
            >
              {
                (userLanguage[0].AssetWorkLevelMaster?.AWLB01 as any)?.[
                  `${commonStore.language.key}`
                ]
              }
            </Button>
            <Button.Or
              text={
                (userLanguage[0].AssetWorkLevelMaster?.AWLB02 as any)?.[
                  `${commonStore.language.key}`
                ]
              }
            />
            <Button
              onClick={saveHandler}
              loading={loader.submit}
              type="submit"
              content="Submit"
              positive
              disabled={!!errorMessage.length || !formDetails.title?.length}
            >
              {
                (userLanguage[0].AssetWorkLevelMaster?.AWLB03 as any)?.[
                  `${commonStore.language.key}`
                ]
              }
            </Button>
          </Button.Group>
        </div>
      }
    </>
  );
}

export default observer(MasterRegisterForm);
